import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Plus, 
  Trash, 
  Pencil, 
  Mail, 
  Clock, 
  User, 
  Users, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Eye
} from "lucide-react";
import { examAssignmentsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import AssignExamModal from "../../components/admin/AssignExamModal";
import EditAssignmentModal from "../../components/admin/EditAssignmentModal";
import { format } from "date-fns";
import ModernPageTemplate, { 
  ModernBadge, 
  ModernLoading,
  ModernPaginationFooter
} from "@/components/common/ModernPageTemplate";

const ExamAssignment = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Constants
  const ASSIGNMENTS_PER_PAGE = 5;
  
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("my"); // Changed from filterType to activeTab, default to "my"
  const [currentPage, setCurrentPage] = useState(1);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(ASSIGNMENTS_PER_PAGE);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);

  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Effect to adjust footer position based on sidebar state
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.admin-fixed-footer');
      if (!footer) return;

      // Check if we're on desktop (lg breakpoint)
      if (window.innerWidth >= 1024) {
        // Look for the main content container to determine sidebar state
        const mainContent = document.querySelector('[class*="lg:pl-"]');
        if (mainContent) {
          const classes = mainContent.className;
          if (classes.includes('lg:pl-14')) {
            // Sidebar is collapsed (68px width)
            footer.style.left = '68px';
          } else if (classes.includes('lg:pl-64')) {
            // Sidebar is expanded (256px width)
            footer.style.left = '256px';
          }
        }
      } else {
        // Mobile/tablet - full width
        footer.style.left = '0px';
      }
    };

    // Initial adjustment
    adjustFooterPosition();

    // Listen for window resize
    window.addEventListener('resize', adjustFooterPosition);

    // Use MutationObserver to watch for class changes on the main content
    const observer = new MutationObserver(adjustFooterPosition);
    const mainContent = document.querySelector('[class*="lg:pl-"]');
    if (mainContent) {
      observer.observe(mainContent, { 
        attributes: true, 
        attributeFilter: ['class'] 
      });
    }

    return () => {
      window.removeEventListener('resize', adjustFooterPosition);
      observer.disconnect();
    };
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortAssignments = (assignments, sortField, sortOrder) => {
    // If no sorting is applied, return assignments in their original order (newest first from API)
    if (!sortField) return assignments;

    return [...assignments].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'dateOfExam') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const getReviewStatusBadge = (status) => {
    const configs = {
      pending: "template",
      reviewed: "language", 
      flagged: "language",
    };
    const config = configs[status] || configs.pending;
    const text = {
      pending: "Under Review",
      reviewed: "Reviewed", 
      flagged: "Flagged"
    }[status] || "Under Review";

    return (
      <ModernBadge variant={config}>
        {text}
      </ModernBadge>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: "template",
      ongoing: "language",
      completed: "template", 
      disqualified: "language",
    };

    return (
      <ModernBadge variant={statusConfig[status] || statusConfig.pending}>
        {status || "pending"}
      </ModernBadge>
    );
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handlePaginationToggle = (checked) => {
    setIsPaginationEnabled(checked);
    setCurrentPage(1); // Reset to first page when toggling pagination
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1); // Reset to first page when changing tab
  };



  // Query for exam assignments - unified query based on active tab
  const {
    data: assignmentsData,
    isLoading: assignmentsLoading,
    isError: assignmentsError,
    refetch: refetchAssignments,
  } = useQuery({
    queryKey: ["exam-assignments", currentPage, searchTerm, activeTab, isPaginationEnabled, rowsPerPage],
    queryFn: () => {
      const params = {
        page: isPaginationEnabled ? currentPage : 1,
        limit: isPaginationEnabled ? rowsPerPage : 1000, // Large number to get all records
        search: searchTerm,
      };
      
      if (activeTab === "my") {
        return examAssignmentsAPI.getMyAssignments(params);
      } else if (activeTab === "company") {
        return examAssignmentsAPI.getCompanyAssignments(params);
      } else {
        // Default to my assignments
        return examAssignmentsAPI.getMyAssignments(params);
      }
    },
    enabled: true,
  });

  // Reset page when search/tab changes
  useEffect(() => {
    setCurrentPage(1);
    // Clear sorting when search/tab changes to show newest first
    setSortField('');
    setSortOrder('asc');
  }, [searchTerm, activeTab]);

  // Refresh function that properly invalidates queries and resets state
  const handleRefresh = async () => {
    // Reset all state to initial values
    setCurrentPage(1);
    setSortField('');
    setSortOrder('asc');
    setSearchTerm('');
    setActiveTab('my'); // Changed from filterType to activeTab
    setIsAssignModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedAssignment(null);
    
    // Invalidate and refetch all related queries
    await queryClient.invalidateQueries({ queryKey: ["exam-assignments"] });
    
    // Show success message
    toast({
      title: "Refreshed",
      description: "Data has been refreshed successfully",
    });
  };



  const handleDeleteAssignment = async (id) => {
    if (!confirm("Are you sure you want to delete this exam assignment?")) return;

    try {
      await examAssignmentsAPI.delete(id);
      toast({
        title: "Assignment deleted",
        description: "The exam assignment has been deleted successfully",
      });
      refetchAssignments();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete assignment",
        variant: "destructive",
      });
    }
  };

  const handleResendInvitation = async (id) => {
    try {
      await examAssignmentsAPI.resendInvitation(id);
      toast({
        title: "Invitation resent",
        description: "The invitation email has been resent successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to resend invitation",
        variant: "destructive",
      });
    }
  };

  const handleEditAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setIsEditModalOpen(true);
  };

  const renderAssignmentsTable = () => {
    const assignments = assignmentsData?.data?.assignments || [];
    const pagination = assignmentsData?.data?.pagination || {};

    // Sort the assignments
    const sortedAssignments = sortAssignments(assignments, sortField, sortOrder);

    return (
      <>
        {assignmentsLoading ? (
          <ModernLoading text="Loading exam assignments..." />
        ) : assignmentsError ? (
          <div className="text-center p-6 text-red-500">
            Error loading exam assignments. Please try again.
          </div>
        ) : sortedAssignments.length > 0 ? (
          <>
            {/* Assignments Table */}
            <div className="admin-table-container">
              <table className="admin-table">
                <thead className="admin-table-header">
                  <tr>
                    <th className="admin-table-cell">S.No</th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('candidateId.name')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Candidate
                        {getSortIcon('candidateId.name')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('candidateId.email')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Email
                        {getSortIcon('candidateId.email')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('examId.name')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Exam
                        {getSortIcon('examId.name')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('dateOfExam')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Scheduled For
                        {getSortIcon('dateOfExam')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('reviewStatus')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Review Status
                        {getSortIcon('reviewStatus')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('assignedBy.name')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Assigned By
                        {getSortIcon('assignedBy.name')}
                      </div>
                    </th>
                    <th className="admin-table-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAssignments.map((assignment, index) => (
                    <tr key={assignment._id} className="admin-table-row">
                      <td className="admin-table-cell number">
                        {(currentPage - 1) * ASSIGNMENTS_PER_PAGE + (index + 1)}
                      </td>
                      <td className="admin-table-cell font-medium">
                        <div className="flex items-center">
                          <User className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                          <span className="text-sm" title={assignment.candidateId?.name}>
                            {assignment.candidateId?.name}
                          </span>
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm" title={assignment.candidateId?.email}>
                          {assignment.candidateId?.email}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm" title={assignment.examId?.name}>
                          {assignment.examId?.name}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        {assignment.dateOfExam ? (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-xs">
                              {format(new Date(assignment.dateOfExam), "MMM dd, yy HH:mm")}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Not scheduled</span>
                        )}
                      </td>
                      <td className="admin-table-cell">
                        {getStatusBadge(assignment.status)}
                      </td>
                      <td className="admin-table-cell">
                        {assignment.reviewStatus ? 
                          getReviewStatusBadge(assignment.reviewStatus) : 
                          <span className="text-gray-400 text-xs">Not Applicable</span>
                        }
                      </td>
                      <td className="admin-table-cell">
                        {assignment.assignedBy ? (
                          <ModernBadge variant="language">
                            <span className="text-xs" title={assignment.assignedBy.name}>
                              {assignment.assignedBy.name}
                            </span>
                          </ModernBadge>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="admin-table-cell text-right">
                        <div className="admin-table-actions">
                          <button
                            className="admin-table-action-btn view"
                            onClick={() => handleResendInvitation(assignment._id)}
                            title="Resend invitation email"
                          >
                            <Mail className="h-3 w-3" />
                          </button>
                          <button
                            className="admin-table-action-btn edit"
                            onClick={() => handleEditAssignment(assignment)}
                            title="Edit assignment"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            className="admin-table-action-btn delete"
                            onClick={() => handleDeleteAssignment(assignment._id)}
                            title="Delete assignment"
                          >
                            <Trash className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center p-6 text-gray-500">
            No exam assignments found
          </div>
        )}
      </>
    );
  };

  return (
    <ModernPageTemplate
      title="Exam Assignment"
      contentClassName="exam-assignment-page"
      stats={{
        total: assignmentsData?.data?.pagination?.total || 0,
        customStats: (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="categories-header-tabs-list">
              <TabsTrigger value="my" className="categories-header-tab">
                <User className="h-3 w-3" />
                My Assignments
              </TabsTrigger>
              <TabsTrigger value="company" className="categories-header-tab">
                <Users className="h-3 w-3" />
                Company Assignments
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )
      }}
      onRefresh={handleRefresh}
      search={{
        placeholder: "Search by candidate name, email, or exam name...",
        value: searchTerm,
        onChange: handleSearchChange,
        onSubmit: (e) => e.preventDefault()
      }}
      createAction={{
        onClick: () => setIsAssignModalOpen(true),
        tooltip: "Assign Exam"
      }}
    >
      {renderAssignmentsTable()}

      {/* Reusable Pagination Footer */}
      <ModernPaginationFooter
        isPaginationEnabled={isPaginationEnabled}
        onPaginationToggle={handlePaginationToggle}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={{
          current: currentPage,
          pages: assignmentsData?.data?.pagination?.totalPages || 1,
          total: assignmentsData?.data?.pagination?.total || 0
        }}
        onPageChange={handlePageChange}
        rowsOptions={[5, 10, 20, 50]}
      />

      <AssignExamModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={() => {
          // Reset to page 1 and clear sorting to show newest assignments first
          setCurrentPage(1);
          setSortField('');
          setSortOrder('asc');
          
          // Refetch assignments
          refetchAssignments();
          
          // Close the modal
          setIsAssignModalOpen(false);
          
          // Show success message
          toast({
            title: "Assignment created",
            description: "The exam assignment has been created successfully and appears at the top of the list",
          });
        }}
      />

      <EditAssignmentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        assignmentId={selectedAssignment?._id}
        onSuccess={() => {
          // Refetch assignments to show updated data
          refetchAssignments();
          
          // Close the modal
          setIsEditModalOpen(false);
          
          // Show success message
          toast({
            title: "Assignment updated",
            description: "The exam assignment has been updated successfully",
          });
        }}
      />
    </ModernPageTemplate>
  );
};

export default ExamAssignment;